import { clearCredentialState, config, oauth, publicCall } from "./api";
import { thumbprint } from "./dpop";
import { dpopKey } from "./keystore";
import * as passkeys from "./passkeys";
import { create } from "./pkce";
import * as session from "./session";
import { b64url } from "./b64";
import type { AgentCreate } from "./agents";

const KEY = "emdur.signin";
const APPROVAL = "emdur.approval";
export interface ApprovalVerification {
  org: string;
  proposal: string;
  etag: string;
  argumentsHash: string;
  personId: string;
  expiresAt: number;
}
export interface AgentVerification {
  action: "create-agent" | "stop-agents" | "change-agent-role";
  org: string;
  argumentsHash: string;
  personId: string;
  expiresAt: number;
  key: string;
  body: AgentCreate | { reason: string } | { role: string };
  agentId?: string;
  etag?: string;
  confirmation?: string;
}
type Verification = ApprovalVerification | AgentVerification;

export function startAgentVerification(
  intent: AgentVerification,
): Promise<never> {
  const detail =
    intent.action === "change-agent-role" && intent.agentId
      ? `/${encodeURIComponent(intent.agentId)}`
      : "";
  return startSignIn(
    `/orgs/${encodeURIComponent(intent.org)}/agents${detail}`,
    intent,
  );
}
export function pendingAgentVerification(
  org: string,
): AgentVerification | null {
  try {
    const proof = JSON.parse(
      sessionStorage.getItem(APPROVAL) ?? "null",
    ) as Verification | null;
    return proof &&
      "action" in proof &&
      proof.org === org &&
      proof.expiresAt > Date.now()
      ? proof
      : null;
  } catch {
    return null;
  }
}
export async function verificationHash(body: unknown): Promise<string> {
  return b64url(
    await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(JSON.stringify(body)),
    ),
  );
}
export function consumeAgentVerification(intent: AgentVerification): boolean {
  return consumeVerification(intent);
}

/** Only non-secret intent metadata is kept across the verified OAuth sign-in. */
export function startApprovalVerification(
  intent: ApprovalVerification,
): Promise<never> {
  return startSignIn("/approvals", intent);
}
export function consumeApprovalVerification(
  intent: ApprovalVerification,
): boolean {
  return consumeVerification(intent);
}
function consumeVerification(intent: Verification): boolean {
  const raw = sessionStorage.getItem(APPROVAL);
  sessionStorage.removeItem(APPROVAL);
  if (!raw) return false;
  try {
    const proof = JSON.parse(raw) as Verification;
    return (
      proof.expiresAt > Date.now() &&
      proof.org === intent.org &&
      proof.argumentsHash === intent.argumentsHash &&
      proof.personId === intent.personId &&
      ("action" in intent
        ? "action" in proof &&
          proof.action === intent.action &&
          proof.key === intent.key &&
          proof.agentId === intent.agentId &&
          proof.etag === intent.etag &&
          proof.confirmation === intent.confirmation
        : "proposal" in proof &&
          proof.proposal === intent.proposal &&
          proof.etag === intent.etag)
    );
  } catch {
    return false;
  }
}
export function hasApprovalVerification(
  org: string,
  proposal: string,
): boolean {
  try {
    const proof = JSON.parse(
      sessionStorage.getItem(APPROVAL) ?? "null",
    ) as ApprovalVerification | null;
    return (
      !!proof &&
      proof.org === org &&
      proof.proposal === proposal &&
      proof.expiresAt > Date.now()
    );
  } catch {
    return false;
  }
}

let startingSignIn: Promise<never> | null = null;
/** Revoke the refresh grant through the stateless relay, then erase credentials even when offline. */
export async function signOut(): Promise<boolean> {
  const token = await session.refreshToken();
  let revoked = !token;
  try {
    if (token)
      revoked = (
        await oauth(
          "revoke",
          new URLSearchParams({ token, token_type_hint: "refresh_token" }),
        )
      ).ok;
  } catch {
    revoked = false;
  } finally {
    clearCredentialState();
    startingSignIn = null;
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(APPROVAL);
    await session.signOut();
  }
  return revoked;
}
export async function freshSignIn(returnTo: string): Promise<never> {
  await signOut();
  return startSignIn(returnTo);
}
/** Concurrent readers of an expired session must not overwrite the OAuth state. */
export function startSignIn(
  returnTo = "/",
  approval?: Verification,
): Promise<never> {
  startingSignIn ??= pushSignIn(returnTo, approval).catch((error: unknown) => {
    startingSignIn = null;
    throw error;
  });
  return startingSignIn;
}

async function pushSignIn(
  returnTo = "/",
  approval?: Verification,
): Promise<never> {
  const generation = session.version();
  const { api_url, client_id } = await config();
  const { verifier, challenge } = await create();
  if (generation !== session.version())
    throw new Error("This sign-in ended. Start again with your passkey.");
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.removeItem(APPROVAL);
  sessionStorage.setItem(
    KEY,
    JSON.stringify({ verifier, state, returnTo, approval }),
  );
  const form = new URLSearchParams({
    response_type: "code",
    redirect_uri: `${location.origin}/callback`,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    dpop_jkt: await thumbprint(await dpopKey()),
  });
  const pushed = await oauth("par", form, generation);
  if (generation !== session.version())
    throw new Error("This sign-in ended. Start again with your passkey.");
  if (!pushed.ok) throw new Error(`sign-in could not start (${pushed.status})`);
  const { request_uri } = (await pushed.json()) as { request_uri: string };
  location.assign(
    `${api_url}/oauth/authorize?client_id=${encodeURIComponent(client_id)}&request_uri=${encodeURIComponent(request_uri)}`,
  );
  return new Promise<never>(() => {});
}

export async function completeSignIn(
  authorizationSession: string,
): Promise<void> {
  const started = await publicCall("POST", "/v1/passkey-assertions", {
    authorization_session: authorizationSession,
  });
  if (!started.ok)
    throw new Error(`sign-in expired or invalid (${started.status})`);
  const { id, public_key } = (await started.json()) as {
    id: string;
    public_key: PublicKeyCredentialRequestOptionsJSON;
  };
  const credential = await passkeys.get(public_key);
  const done = await publicCall(
    "POST",
    `/v1/passkey-assertions/${id}/complete`,
    { credential },
  );
  if (!done.ok) throw new Error(`passkey not accepted (${done.status})`);
  location.assign(((await done.json()) as { next: string }).next);
}

export async function finishCallback(search: string): Promise<string> {
  const generation = session.version();
  const { api_url } = await config();
  const params = new URLSearchParams(search);
  const pending = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as {
    verifier: string;
    state: string;
    returnTo: string;
    approval?: Verification;
  } | null;
  if (!pending || params.get("state") !== pending.state)
    throw new Error("sign-in state does not match");
  if (params.get("iss") !== api_url) throw new Error("unexpected issuer");
  const r = await oauth(
    "token",
    new URLSearchParams({
      grant_type: "authorization_code",
      code: params.get("code") ?? "",
      code_verifier: pending.verifier,
      redirect_uri: `${location.origin}/callback`,
    }),
    generation,
  );
  if (!r.ok) throw new Error(`sign-in failed (${r.status})`);
  await session.setTokens(await r.json(), generation);
  if (generation !== session.version())
    throw new Error("This sign-in ended. Start again with your passkey.");
  if (pending.approval && pending.approval.expiresAt > Date.now())
    sessionStorage.setItem(APPROVAL, JSON.stringify(pending.approval));
  sessionStorage.removeItem(KEY);
  return pending.returnTo;
}
